import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Axios } from '../../axios-instance';
import { BaseError } from '@utils/types/shared/base';
import {
  IssueLicensePayload,
  IssueLicenseResponse
} from '@utils/types/back-office/licensing';
import { ADMIN_LICENSE_REQUESTS_QUERY_KEY } from './use-get-admin-license-requests';
import { ADMIN_LICENSE_KEYS_QUERY_KEY } from './use-get-admin-license-keys';

interface IssueLicenseParams {
  requestId: number;
  payload: IssueLicensePayload;
}

const issueLicense = async ({
  requestId,
  payload
}: IssueLicenseParams): Promise<IssueLicenseResponse> => {
  const response = await Axios.post<IssueLicenseResponse>(
    `api/admin/licensing/requests/${requestId}/issue`,
    payload
  );
  return response.data;
};

export const useIssueLicense = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    IssueLicenseResponse,
    AxiosError<BaseError>,
    IssueLicenseParams
  >(issueLicense, {
    onSuccess: () => {
      toast.success(t('license-issued-successfully'));
      queryClient.invalidateQueries([ADMIN_LICENSE_REQUESTS_QUERY_KEY]);
      queryClient.invalidateQueries([ADMIN_LICENSE_KEYS_QUERY_KEY]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const data = error.response?.data;
      let errorMessage = t('failed-to-issue-license');

      if (data?.error === 'already_processed') {
        errorMessage = t('already-processed');
      } else if (data?.error === 'licensing_server_error') {
        errorMessage = t('licensing-server-error');
      } else if (data?.error_description) {
        errorMessage = data.error_description;
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (data?.detail) {
        errorMessage = data.detail;
      }

      toast.error(errorMessage);
    }
  });
};
